import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Store } from 'lucide-react';
import { ProductCard } from '@/components/products/ProductCard';
import { ProductDrawer } from '@/components/products/ProductDrawer';
import { useCart } from '@/hooks/useCart';
import Loader from '@/components/common/Loader';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import { useVendor } from '@/contexts/VendorContext';
import { SEO } from '@/components/common/SEO';
import { PwaPrompt } from '../components/modals/PwaPrompt';
import { useSwipeable } from 'react-swipeable';
import axiosInstance from '../utils/axiosInstance';
import HeroHeader from './user/HeroHeader';
import { useUser } from '../contexts/UserContext';
import { toast } from 'react-toastify';
import { FloatingCartButton } from '../components/cart/FloatingCartButton';
import { CartDrawer } from '../components/products/CartDrawer';
import Drawer from '../components/products/Drawer';

function DashboardContent() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showPrompt, setShowPrompt] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isEmpty, setIsEmpty] = useState(false);

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      const currCategory = activeTab;
      const idx = categories.findIndex((i) => i.id === currCategory.id);

      if (eventData?.dir === 'Right' && idx > 0) {
        setActiveTab(categories.at(idx - 1));
      } else if (eventData?.dir === 'Left' && idx < categories?.length - 1) {
        setActiveTab(categories.at(idx + 1));
      }
    },
  });

  const { user, refreshUser } = useUser();
  const { selectedVendor, vendors, setSelectedVendor } = useVendor();

  const sessionToken = useMemo(() => localStorage.getItem('sessionToken'), []);
  const refreshToken = useMemo(() => localStorage.getItem('refreshToken'), []);

  const headers = useMemo(
    () => ({
      sessionToken,
      refreshToken,
    }),
    [sessionToken, refreshToken]
  );

  const fetchCategories = async () => {
    try {
      const { data } = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products/categories`,
        { headers }
      );
      let vendorCategories;
      if (user?.userType === 'vendor') {
        vendorCategories = user?.vendorCategories || [];
      } else {
        const selectedVendor = JSON.parse(localStorage.getItem('selectedVendor'));
        vendorCategories = selectedVendor?.vendorCategories || [];
      }
      const syncedCategories = data.filter((category) => vendorCategories.includes(category.name));

      setCategories(syncedCategories);
      setActiveTab(syncedCategories[0] || null);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    if (!user || !categories?.length || !activeTab) return;

    const vendorId =
      user?.userType === 'user'
        ? JSON.parse(localStorage.getItem('selectedVendor'))?.vendorId
        : user?.id;

    if (!vendorId && user?.userType === 'user') return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products?category=${activeTab?.id}`,
        {
          headers: { ...headers, 'X-Vendor-Id': vendorId },
        }
      );

      const isNotAvailable = response?.data?.reduce((acc, data) => {
        if (data?.available) {
          acc = false; // If available, set the accumulator to false
        }
        return acc; // Return the accumulator to be used in the next iteration
      }, true);
      if (isNotAvailable) {
        setIsEmpty(true);
        setProducts([]);
        return;
      }
      setIsEmpty(false);

      const sortedProducts = response?.data?.sort((a, b) => {
        if (a?.available === b?.available) {
          return a?.productName.localeCompare(b?.productName);
        }
        return a?.available ? -1 : 1;
      });

      setProducts(sortedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNearbyVendors = useCallback(async () => {
    try {
      const { data: favVendors } = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/user/vendors?userId=${user?.id}`,
        { headers }
      );

      const favoritesResponse = favVendors.at(0);

      let selectedData;

      selectedData = await handleExistingVendor(vendors, favoritesResponse, user, headers);

      if (selectedData) {
        setCategories(selectedData.vendorCategories);
        setActiveTab(selectedData?.vendorCategories[0]);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, [user]);

  const handleExistingVendor = async (vendors, favorites, user, headers) => {
    const selectedVendor = JSON.parse(localStorage.getItem('selectedVendor'));

    if (selectedVendor?.vendorId !== favorites.vendorId || !selectedVendor) {
      toast.info('Please select a vendor first');
      navigate('/');
    }
    return selectedVendor;
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setIsDrawerOpen(true);
  };

  const tabsListRef = useRef(null);

  useEffect(() => {
    if (tabsListRef.current && activeTab) {
      const tabsList = tabsListRef.current;
      const activeTabElement = tabsList.querySelector(`[data-state="active"]`);

      if (activeTabElement) {
        const tabsListRect = tabsList.getBoundingClientRect();
        const activeTabRect = activeTabElement.getBoundingClientRect();

        const scrollLeft =
          activeTabElement.offsetLeft - tabsListRect.width / 2 + activeTabRect.width / 2;

        tabsList.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    refreshCart();

    if (user?.userType === 'user') {
      fetchCategories();
    } else if (user?.userType === 'vendor') {
      fetchCategories();
    } else {
      const contactNo = localStorage.getItem('contactNo');
      refreshUser(contactNo);
    }
  }, [user]);

  useEffect(() => {
    if (categories?.length > 0 && user && activeTab) {
      fetchProducts();
    }
  }, [user, activeTab, categories]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      setInstallPrompt(e);
      if (!localStorage.getItem('pwaPromptDismissed')) {
        setShowPrompt(true);
      }
    };

    window.scrollTo(0, 0);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return (
    <div className="min-h-screen sm:border-l sm:border-r bg-white">
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-500 text-white flex items-center justify-between px-4 py-2 text-sm font-semibold shadow-md animate-slideDown">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12A9 9 0 1 1 3 12a9 9 0 0 1 18 0z"
              />
            </svg>
            <span>You are offline. Some features may not work properly.</span>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-white text-red-500 px-2 py-1 rounded-md text-xs font-bold hover:bg-red-100 transition"
          >
            Retry
          </button>
        </div>
      )}
      <PwaPrompt
        installPrompt={installPrompt}
        showPrompt={showPrompt}
        setShowPrompt={setShowPrompt}
        setInstallPrompt={setInstallPrompt}
      />
      <SEO
        title="Store - Let's make Local Big"
        description="Order our fresh selection of fruits, vegetables, and dairy products. High-quality produce delivered to your doorstep in Dwarka, Delhi."
        canonicalUrl="https://www.biglocal.in/"
        ogImage="/images/biglocallogo.png"
      />
      <div className="max-w-md mx-auto">
        <HeroHeader setCategories={setCategories} />
        <div>
          <div
            className={`sticky top-16 z-10 transition-all duration-300 ${user?.userType === 'user' ? 'min-h-32' : 'min-h-20'} bg-white pb-1 pt-1 px-2`}
          >
            {user?.userType === 'user' && selectedVendor && (
              <div className="bg-white animate-fadeIn">
                <div className="px-4 py-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-50 rounded-full">
                        <Store className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h2 className="font-medium line-clamp-1 text-gray-900">
                          {selectedVendor?.shopName || `${selectedVendor?.vendorName}'s Store`}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Tabs
              defaultValue={activeTab}
              onValueChange={setActiveTab}
              className="w-full overflow-x-auto"
            >
              <TabsList
                ref={tabsListRef}
                className="w-full h-fit bg-transparent pr-5 py-0 gap-1 overflow-x-auto scrollbar-hide justify-start flex snap-x snap-mandatory scroll-smooth"
              >
                {categories?.map((category) => (
                  <TabsTrigger
                    key={category.id}
                    value={category}
                    data-state={category.name === activeTab?.name ? 'active' : 'inactive'}
                    className="py-0 px-0 mx-0 my-0 items-center justify-center w-fit flex-row data-[state=active]:text-white data-[state=active]:shadow-none data-[state=active]:bg-transparent transition-all duration-300 rounded-none snap-center"
                  >
                    <div className="">
                      <div
                        className={`${
                          activeTab.name === category.name
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-black hover:bg-green-50'
                        } min-w-20 rounded-md shadow-sm px-3 pt-0 pb-2 flex flex-col items-center border transition-all duration-300`}
                      >
                        <div className="px-2 pt-2 pb-1 rounded-full mb-1">
                          <img
                            loading="lazy"
                            src={`/${category.name}.png`}
                            alt={category.name}
                            className="w-8 h-8"
                          />
                        </div>
                        <span className="text-xs font-medium">{category.name}</span>
                      </div>
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <main id="product-item" className="relative mx-auto px-4 py-2 pb-20" {...handlers}>
            <div className="grid grid-cols-2 gap-4">
              {isLoading || categories?.length === 0 ? (
                <div className="absolute top-10 z-20 max-w-md h-full left-1/2 -translate-x-1/2 w-full flex items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <>
                  {isEmpty ? (
                    <div className="col-span-2 py-8 text-center">
                      <div className="bg-amber-50 rounded-lg p-4 shadow-sm border border-amber-100">
                        <h3 className="text-amber-700 font-medium mb-1">Category Unavailable</h3>
                        <p className="text-amber-600 text-sm">
                          Products in {activeTab?.name} category are currently unavailable. They may
                          be added soon. Please check other categories.
                        </p>
                      </div>
                    </div>
                  ) : (
                    user?.userType === 'user' &&
                    products?.map((product) => (
                      <ProductCard
                        key={product.productId}
                        product={product}
                        onSelect={handleProductSelect}
                        category={activeTab.name}
                      />
                    ))
                  )}
                  {user?.userType === 'vendor' && (
                    <div className="grid col-span-2 grid-cols-2 gap-4">
                      {products?.map((product) => (
                        <ProductCard
                          key={product.productId}
                          product={product}
                          onSelect={handleProductSelect}
                          category={activeTab?.name}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        <Drawer
          product={selectedProduct}
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedProduct(null);
          }}
          isCartBlocked={false}
          category={activeTab}
        />

        <FloatingCartButton />
      </div>

      <BottomNav page="dashboard" />
    </div>
  );
}

export default DashboardContent;
